import React from 'react';
import { AlertTriangle, CreditCard } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { cn } from '../../lib/utils';
import { useBilling } from '../../Context/BillingContext';

function PaymentStatusBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    loading,
    subscriptions,
    hasPaymentIssue,
    needsPaymentMethodUpdate,
    expiredPaymentMethod,
  } = useBilling();

  const hiddenPrefixes = ['/', '/landing', '/privacy-policy', '/terms-and-condition', '/callback'];
  const shouldHide =
    hiddenPrefixes.some(prefix =>
      prefix === '/' ? location.pathname === '/' : location.pathname.startsWith(prefix)
    ) || location.pathname.startsWith('/admin');

  if (loading || shouldHide || (!hasPaymentIssue && !needsPaymentMethodUpdate)) {
    return null;
  }

  const failedCount = subscriptions.filter(
    item => item.status === 'past_due' || item.status === 'incomplete'
  ).length;
  const title = hasPaymentIssue
    ? 'Payment failed on a subscription'
    : expiredPaymentMethod
      ? 'Payment method expired'
      : 'Payment method needs attention';
  const message = hasPaymentIssue
    ? `${failedCount} subscription(s) could not be charged. Update your card or retry payment to keep campaigns running.`
    : expiredPaymentMethod
      ? 'Your card on file has expired. Update your payment method so monthly campaigns can renew without interruption.'
      : 'Add or update your payment method so monthly campaigns can renew without interruption.';

  return (
    <div
      className={cn(
        'mb-[1.125rem] flex items-center gap-[0.875rem] rounded-[var(--sp-radius-lg)] border p-[0.875rem] sm:p-[1.125rem]',
        'max-[45rem]:flex-col max-[45rem]:items-stretch',
        hasPaymentIssue
          ? 'border-sp-danger/30 bg-gradient-to-r from-red-50 to-rose-50 text-sp-danger'
          : 'border-sp-warning/30 bg-gradient-to-r from-amber-50 to-yellow-50 text-sp-warning'
      )}
      role="alert"
    >
      <div
        className={cn(
          'inline-flex h-[2.5rem] w-[2.5rem] shrink-0 items-center justify-center rounded-[var(--sp-radius-md)]',
          hasPaymentIssue ? 'bg-sp-danger/15 text-sp-danger' : 'bg-sp-warning/15 text-sp-warning'
        )}
      >
        {hasPaymentIssue ? <AlertTriangle size={20} /> : <CreditCard size={20} />}
      </div>
      <div className="min-w-0 flex-1">
        <strong className="block text-sm font-semibold">{title}</strong>
        <p className="m-0 text-[0.8125rem] leading-relaxed opacity-90">{message}</p>
      </div>
      <div className="shrink-0 max-[45rem]:w-full">
        <button
          type="button"
          onClick={() => navigate('/dashboard/billing')}
          className={cn(
            'w-full cursor-pointer rounded-full border-none px-4 py-2.5 text-[0.8125rem] font-bold text-white whitespace-nowrap sm:w-auto',
            hasPaymentIssue ? 'bg-sp-danger hover:bg-sp-danger/90' : 'bg-sp-warning hover:bg-sp-warning/90'
          )}
        >
          Go to billing
        </button>
      </div>
    </div>
  );
}

export default PaymentStatusBanner;
